import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PageContainer from '../PageContainer';
import ChatPanel from '../Partner/Community/widgets/ChatPanel';
import { buildChatMessagesFromPages } from '../Partner/Community/mapPartnerGroupChannel';
import { setPartnerCommunityLiveView } from '../Partner/Community/partnerCommunityLiveView';
import { NumberedButton } from '../../styles/StyledComponents';
import { useAriaEscalationsQuery } from '../../api/use-aria-escalations-query';
import { useTakeOverAriaEscalationMutation } from '../../api/use-take-over-aria-escalation-mutation';
import { useHandBackAriaEscalationMutation } from '../../api/use-hand-back-aria-escalation-mutation';
import { useSendPartnerGroupChatMessageMutation } from '../../api/use-send-partner-group-chat-message-mutation';
import { usePartnerGroupChannelMessagesQuery } from '../../api/use-partner-group-channel-query';
import { useAuth } from '../../contexts/AuthContext';
import {
  ARIA_ALL_QUEUE,
  ARIA_ESCALATION_API_STATUS,
  ARIA_ESCALATION_QUEUE_MAP,
  ARIA_ESCALATIONS_PAGE_SIZE,
  DEFAULT_ARIA_ESCALATION_QUEUE,
  DEFAULT_ARIA_ESCALATION_STATUS,
  getAriaQueueNavItems,
} from './ariaHandsoffConstants';
import AriaChatList from './widgets/AriaChatList';
import AriaChatFooter, { AriaTakenOverBanner } from './widgets/AriaChatFooter';
import AriaHandoffToolbar from './widgets/AriaHandoffToolbar';
import AriaQueueNav from './widgets/AriaQueueNav';
import { ariaColumnDividerSx, ariaShellSx } from './ariaHandsoffSurfaces';

const mergeRoomMessages = (apiMessages = [], localMessages = []) => {
  if (!localMessages.length) return apiMessages;

  const seen = new Set(apiMessages.map((message) => message.id).filter(Boolean));
  const extras = localMessages.filter((message) => message?.id && !seen.has(message.id));
  if (!extras.length) return apiMessages;
  return [...apiMessages, ...extras];
};

const AriaHandsoff = () => {
  const { isPartner } = useAuth();
  const queueNavItems = useMemo(() => getAriaQueueNavItems(isPartner), [isPartner]);
  const [activeQueue, setActiveQueue] = useState(DEFAULT_ARIA_ESCALATION_QUEUE);
  const [activeStatus, setActiveStatus] = useState(DEFAULT_ARIA_ESCALATION_STATUS);
  const [page, setPage] = useState(1);
  const [selectedChatId, setSelectedChatId] = useState(null);
  /**
   * Keeps the open room across take-over / hand-back when the case leaves the
   * current status filter until the destination tab list includes it again.
   */
  const preserveSelectionRef = useRef(false);
  const [stickySelectedChat, setStickySelectedChat] = useState(null);
  const [mobileShowRoom, setMobileShowRoom] = useState(false);
  /** Local UI overrides (e.g. take-over status) until list refetch catches up. */
  const [localOverrides, setLocalOverrides] = useState({});

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit: ARIA_ESCALATIONS_PAGE_SIZE,
      status: activeStatus || DEFAULT_ARIA_ESCALATION_STATUS,
    };
    if (activeQueue && activeQueue !== ARIA_ALL_QUEUE) {
      params.queue = activeQueue;
    }
    return params;
  }, [activeQueue, activeStatus, page]);

  const { data, isLoading, isFetching, isError, error } = useAriaEscalationsQuery(queryParams);
  const takeOverMutation = useTakeOverAriaEscalationMutation();
  const handBackMutation = useHandBackAriaEscalationMutation();
  const sendChatMessageMutation = useSendPartnerGroupChatMessageMutation();

  const showListSkeleton = isLoading || isFetching;

  const queueCounts = data?.counts?.byQueue ?? {};
  const allOpenCount = isPartner
    ? queueNavItems.reduce((sum, queue) => {
        if (queue.value === ARIA_ALL_QUEUE) return sum;
        return sum + Number(queueCounts[queue.value] ?? 0);
      }, 0)
    : Number(data?.counts?.all ?? 0);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Number(data?.totalPages ?? 1));
  const currentPage = Math.min(page, totalPages);

  const chats = useMemo(() => {
    const escalations = data?.escalations ?? [];
    return escalations.map((escalation) => {
      const override = localOverrides[escalation.id];
      if (!override) return escalation;
      return {
        ...escalation,
        ...override,
        messages: escalation.messages,
      };
    });
  }, [data?.escalations, localOverrides]);

  const selectedChat = useMemo(() => {
    const fromList = chats.find((chat) => chat.id === selectedChatId) ?? null;
    if (fromList) return fromList;

    if (!selectedChatId || stickySelectedChat?.id !== selectedChatId) return null;

    const override = localOverrides[selectedChatId];
    if (!override) return stickySelectedChat;

    return {
      ...stickySelectedChat,
      ...override,
      messages: stickySelectedChat.messages,
    };
  }, [chats, localOverrides, selectedChatId, stickySelectedChat]);

  const selectedStreamChannelId = selectedChat?.streamChannelId ?? null;
  const selectedResolvedAt =
    String(selectedChat?.apiStatus ?? '').toLowerCase() === ARIA_ESCALATION_API_STATUS.RESOLVED
      ? (selectedChat?.resolvedAtRaw ?? null)
      : null;

  const {
    data: messagesPages,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError: isMessagesError,
    error: messagesError,
  } = usePartnerGroupChannelMessagesQuery({
    enabled: Boolean(selectedStreamChannelId),
    channelId: selectedStreamChannelId,
    limit: 20,
    resolvedAt: selectedResolvedAt,
  });

  const apiRoomMessages = useMemo(
    () => buildChatMessagesFromPages(messagesPages?.pages),
    [messagesPages?.pages],
  );

  const roomMessages = useMemo(() => {
    const localMessages = selectedChatId ? (localOverrides[selectedChatId]?.messages ?? []) : [];
    return mergeRoomMessages(apiRoomMessages, localMessages);
  }, [apiRoomMessages, localOverrides, selectedChatId]);

  const activeQueueMeta = ARIA_ESCALATION_QUEUE_MAP[activeQueue];
  const apiStatus = String(selectedChat?.apiStatus ?? '').toLowerCase();
  const canReplyAsAdmin = apiStatus === ARIA_ESCALATION_API_STATUS.IN_PROGRESS;
  const showMessagesSkeleton = Boolean(selectedStreamChannelId) && isMessagesLoading;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (showListSkeleton) return;
    if (!selectedChatId) return;

    const stillVisible = chats.some((chat) => chat.id === selectedChatId);
    if (stillVisible) {
      preserveSelectionRef.current = false;
      setStickySelectedChat(null);
      return;
    }

    // Take-over / hand-back: keep room open while the destination status list loads.
    if (preserveSelectionRef.current) return;

    setSelectedChatId(null);
    setStickySelectedChat(null);
    setMobileShowRoom(false);
  }, [chats, selectedChatId, showListSkeleton]);

  useEffect(() => {
    const isRoomActive = Boolean(selectedStreamChannelId);

    setPartnerCommunityLiveView({
      active: isRoomActive,
      streamChannelId: selectedStreamChannelId,
      gatewayChannelId: selectedStreamChannelId,
    });

    return () => {
      setPartnerCommunityLiveView({
        active: false,
        streamChannelId: null,
        gatewayChannelId: null,
      });
    };
  }, [selectedStreamChannelId]);

  const patchLocalChat = (chatId, patch) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [chatId]: {
        ...(prev[chatId] ?? {}),
        ...patch,
      },
    }));
  };

  const clearRoomSelection = () => {
    preserveSelectionRef.current = false;
    setStickySelectedChat(null);
    setSelectedChatId(null);
    setMobileShowRoom(false);
  };

  const handleSelectQueue = (nextQueue) => {
    if (!nextQueue || nextQueue === activeQueue) return;
    setActiveQueue(nextQueue);
    setPage(1);
    clearRoomSelection();
  };

  const handleSelectStatus = (nextStatus) => {
    if (!nextStatus || nextStatus === activeStatus) return;
    setActiveStatus(nextStatus);
    setPage(1);
    clearRoomSelection();
  };

  const handleSelectChat = (chatId) => {
    preserveSelectionRef.current = false;
    setStickySelectedChat(null);
    setSelectedChatId(chatId);
    setMobileShowRoom(true);
    patchLocalChat(chatId, { unreadCount: 0 });
  };

  const moveSelectionToStatus = (chat, nextStatus) => {
    if (!chat?.id || !nextStatus) return;

    preserveSelectionRef.current = true;
    setStickySelectedChat(chat);
    setSelectedChatId(chat.id);
    setMobileShowRoom(true);
    setPage(1);
    setActiveStatus(nextStatus);
  };

  const handleTakeHandover = () => {
    if (!selectedChat?.id || takeOverMutation.isPending) return;

    const chatSnapshot = selectedChat;
    const previousStatus = activeStatus;
    moveSelectionToStatus(chatSnapshot, ARIA_ESCALATION_API_STATUS.IN_PROGRESS);

    takeOverMutation.mutate(
      {
        escalationId: chatSnapshot.id,
        previousChat: chatSnapshot,
      },
      {
        onSuccess: (result) => {
          const chat = result?.chat;
          if (!chat?.id) return;
          patchLocalChat(chat.id, {
            ...chat,
            unreadCount: 0,
          });
          setStickySelectedChat(chat);
          setSelectedChatId(chat.id);
        },
        onError: () => {
          preserveSelectionRef.current = true;
          setStickySelectedChat(chatSnapshot);
          setSelectedChatId(chatSnapshot.id);
          setActiveStatus(previousStatus);
        },
      },
    );
  };

  const handleHandBack = () => {
    if (!selectedChat?.id || handBackMutation.isPending) return;

    const chatSnapshot = selectedChat;
    const previousStatus = activeStatus;
    // Optimistic destination; corrected from API status on success if still in_progress.
    moveSelectionToStatus(chatSnapshot, ARIA_ESCALATION_API_STATUS.RESOLVED);

    handBackMutation.mutate(
      {
        escalationId: chatSnapshot.id,
        previousChat: chatSnapshot,
      },
      {
        onSuccess: (result) => {
          const chat = result?.chat;
          if (!chat?.id) return;
          patchLocalChat(chat.id, {
            ...chat,
            unreadCount: 0,
          });
          const nextStatus = String(chat.apiStatus ?? '').toLowerCase();
          const destinationStatus =
            nextStatus === ARIA_ESCALATION_API_STATUS.IN_PROGRESS
              ? ARIA_ESCALATION_API_STATUS.IN_PROGRESS
              : ARIA_ESCALATION_API_STATUS.RESOLVED;
          moveSelectionToStatus(chat, destinationStatus);
        },
        onError: () => {
          preserveSelectionRef.current = true;
          setStickySelectedChat(chatSnapshot);
          setSelectedChatId(chatSnapshot.id);
          setActiveStatus(previousStatus);
        },
      },
    );
  };

  const handleSendMessage = async (messageText) => {
    if (!selectedChat || !canReplyAsAdmin || !selectedStreamChannelId) return;
    if (sendChatMessageMutation.isPending) return;

    await sendChatMessageMutation.mutateAsync({
      messageText,
      attachments: [],
      channelId: selectedStreamChannelId,
    });

    patchLocalChat(selectedChat.id, {
      lastMessageAt: new Date().toISOString(),
      lastMessagePreview: String(messageText ?? '').trim(),
      unreadCount: 0,
    });
  };

  return (
    <PageContainer
      title="Aria Handsoff"
      contentSx={{
        p: { xs: 1, sm: 1.5, md: 2 },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'calc(100vh - 88px)', md: 'calc(100vh - 100px)' },
        borderRadius: { xs: '12px', md: '16px' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: 0,
          ...ariaShellSx,
          borderRadius: { xs: '10px', md: '12px' },
          overflow: 'hidden',
        }}
      >
        {/* Desktop / tablet rail */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            minHeight: 0,
            flexShrink: 0,
          }}
        >
          <AriaQueueNav
            variant="rail"
            navItems={queueNavItems}
            activeQueue={activeQueue}
            queueCounts={queueCounts}
            allCount={allOpenCount}
            onSelectQueue={handleSelectQueue}
          />
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 300, lg: 320 },
            flexShrink: 0,
            display: { xs: mobileShowRoom ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
          }}
        >
          {/* Mobile / small tablet queue strip */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, flexShrink: 0 }}>
            <AriaQueueNav
              variant="strip"
              navItems={queueNavItems}
              activeQueue={activeQueue}
              queueCounts={queueCounts}
              allCount={allOpenCount}
              onSelectQueue={handleSelectQueue}
            />
          </Box>

          {isError && !showListSkeleton ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                ...ariaColumnDividerSx,
              }}
            >
              <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                {error?.message || 'Failed to load escalations'}
              </Typography>
            </Box>
          ) : (
            <>
              <AriaChatList
                chats={chats}
                selectedChatId={selectedChatId}
                queueLabel={activeQueueMeta?.label}
                activeStatus={activeStatus}
                isLoading={showListSkeleton}
                onSelectChat={handleSelectChat}
                onStatusChange={handleSelectStatus}
              />
              {!showListSkeleton && totalPages > 1 ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1,
                    py: 1.25,
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    ...ariaColumnDividerSx,
                  }}
                >
                  <NumberedButton
                    onClick={() => setPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage <= 1}
                  >
                    <KeyboardDoubleArrowRightIcon sx={{ transform: 'rotate(180deg)' }} />
                  </NumberedButton>
                  <NumberedButton active>{currentPage}</NumberedButton>
                  <NumberedButton
                    onClick={() => setPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage >= totalPages}
                  >
                    <KeyboardDoubleArrowRightIcon />
                  </NumberedButton>
                </Box>
              ) : null}
              {!showListSkeleton && total > 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    pb: 1.25,
                    color: 'text.disabled',
                    ...ariaColumnDividerSx,
                  }}
                >
                  {total} in this queue filter
                </Typography>
              ) : null}
            </>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: { xs: mobileShowRoom ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          {!selectedChat ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: { xs: 2, md: 3 },
                color: 'text.secondary',
                bgcolor: 'background.default',
              }}
            >
              <ForumOutlinedIcon sx={{ fontSize: { xs: 36, md: 40 }, color: 'action.disabled' }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  textAlign: 'center',
                  fontSize: '0.9375rem',
                }}
              >
                Select a conversation
              </Typography>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', maxWidth: 320, px: 1, fontSize: '0.8125rem' }}
              >
                Choose a chat in this queue to review Aria’s thread and take over when needed.
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  px: 1.5,
                  pt: 1.25,
                  pb: 0.5,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              >
                <Typography
                  component="button"
                  type="button"
                  onClick={() => setMobileShowRoom(false)}
                  sx={{
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'warning.main',
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    p: 0.5,
                    m: 0,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  ← Back to conversations
                </Typography>
              </Box>

              <AriaHandoffToolbar chat={selectedChat} />

              {!selectedStreamChannelId ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    This escalation has no stream channel id, so messages cannot be loaded.
                  </Typography>
                </Box>
              ) : isMessagesError ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                  }}
                >
                  <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                    {messagesError?.message || 'Failed to load chat messages'}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                    <ChatPanel
                      communityName={selectedChat.userName}
                      communityLogo={selectedChat.avatarUrl}
                      channelId={selectedStreamChannelId}
                      messages={roomMessages}
                      isLoading={showMessagesSkeleton}
                      hasMore={Boolean(hasNextPage)}
                      loadingMore={isFetchingNextPage}
                      onLoadMore={() => fetchNextPage()}
                      compact
                      alignMode="support"
                      fillHeight
                      embedded
                      hideHeader
                      showLockIcon={false}
                      enableReactions={false}
                      enableReactionOpener={false}
                      sending={sendChatMessageMutation.isPending}
                      composerPlaceholder="Write a reply..."
                      composerBanner={
                        canReplyAsAdmin ? (
                          <AriaTakenOverBanner
                            memberName={selectedChat.userName}
                            onHandBack={handleHandBack}
                            handingBack={handBackMutation.isPending}
                          />
                        ) : null
                      }
                      onSendMessage={canReplyAsAdmin ? handleSendMessage : undefined}
                    />
                  </Box>
                  <AriaChatFooter
                    apiStatus={selectedChat.apiStatus}
                    onTakeHandover={handleTakeHandover}
                    takingOver={takeOverMutation.isPending}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </PageContainer>
  );
};

export default AriaHandsoff;
